import { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

// Common currencies with symbols
const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal' },
  { code: 'BHD', symbol: 'BD', name: 'Bahraini Dinar' },
  { code: 'OMR', symbol: 'ر.ع', name: 'Omani Rial' },
  { code: 'JOD', symbol: 'JD', name: 'Jordanian Dinar' },
  { code: 'IQD', symbol: 'ع.د', name: 'Iraqi Dinar' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
  { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev' },
  { code: 'HRK', symbol: 'kn', name: 'Croatian Kuna' },
  { code: 'ISK', symbol: 'kr', name: 'Icelandic Krona' },
  { code: 'CLP', symbol: 'CL$', name: 'Chilean Peso' },
  { code: 'COP', symbol: 'CO$', name: 'Colombian Peso' },
  { code: 'PEN', symbol: 'S/', name: 'Peruvian Sol' },
  { code: 'ARS', symbol: 'AR$', name: 'Argentine Peso' },
  { code: 'TWD', symbol: 'NT$', name: 'Taiwan Dollar' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi' },
  { code: 'MAD', symbol: 'MAD', name: 'Moroccan Dirham' },
  { code: 'TND', symbol: 'DT', name: 'Tunisian Dinar' },
  { code: 'LBP', symbol: 'ل.ل', name: 'Lebanese Pound' },
  { code: 'DZD', symbol: 'د.ج', name: 'Algerian Dinar' },
];

// Map country codes to currency codes
const COUNTRY_CURRENCY_MAP = {
  'US': 'USD', 'GB': 'GBP', 'DE': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR',
  'JP': 'JPY', 'AE': 'AED', 'SA': 'SAR', 'TR': 'TRY', 'IN': 'INR', 'CN': 'CNY',
  'KR': 'KRW', 'BR': 'BRL', 'CA': 'CAD', 'AU': 'AUD', 'CH': 'CHF', 'MX': 'MXN',
  'SE': 'SEK', 'NO': 'NOK', 'DK': 'DKK', 'PL': 'PLN', 'TH': 'THB', 'ID': 'IDR',
  'MY': 'MYR', 'SG': 'SGD', 'HK': 'HKD', 'ZA': 'ZAR', 'RU': 'RUB', 'EG': 'EGP',
  'NG': 'NGN', 'KW': 'KWD', 'QA': 'QAR', 'BH': 'BHD', 'OM': 'OMR', 'JO': 'JOD',
  'IQ': 'IQD', 'PK': 'PKR', 'BD': 'BDT', 'PH': 'PHP', 'VN': 'VND', 'NZ': 'NZD',
  'CZ': 'CZK', 'HU': 'HUF', 'RO': 'RON', 'BG': 'BGN', 'HR': 'HRK', 'IS': 'ISK',
  'CL': 'CLP', 'CO': 'COP', 'PE': 'PEN', 'AR': 'ARS', 'TW': 'TWD', 'KE': 'KES',
  'GH': 'GHS', 'MA': 'MAD', 'TN': 'TND', 'LB': 'LBP', 'DZ': 'DZD',
  'NL': 'EUR', 'BE': 'EUR', 'AT': 'EUR', 'PT': 'EUR', 'IE': 'EUR', 'FI': 'EUR', 'GR': 'EUR',
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem('selected_currency');
    if (saved) {
      setCurrency(saved);
    } else {
      detectCurrencyByLocation();
    }
    fetchRates();
  }, []);

  const detectCurrencyByLocation = async () => {
    try {
      const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) });
      const data = await res.json();
      if (data.country_code) {
        const detected = COUNTRY_CURRENCY_MAP[data.country_code] || 'USD';
        setCurrency(detected);
        localStorage.setItem('selected_currency', detected);
      }
    } catch (err) {
      // Geo-IP lookup blocked or offline — default to USD silently is fine,
      // but log so devs can spot widespread failures.
      console.debug('Currency geo-detection failed:', err?.message || err);
    }
  };

  const fetchRates = async () => {
    try {
      // Use open exchange rate API (free)
      const res = await fetch('https://open.er-api.com/v6/latest/USD', { signal: AbortSignal.timeout(8000) });
      const data = await res.json();
      if (data.rates) {
        setRates(data.rates);
        localStorage.setItem('exchange_rates', JSON.stringify({ rates: data.rates, ts: Date.now() }));
      }
    } catch (err) {
      console.warn('Exchange-rate fetch failed, falling back to cache:', err?.message || err);
      // Try to load from cache
      try {
        const cached = JSON.parse(localStorage.getItem('exchange_rates'));
        if (cached?.rates) setRates(cached.rates);
      } catch (parseErr) {
        console.warn('Could not parse cached exchange rates:', parseErr?.message || parseErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const changeCurrency = (code) => {
    setCurrency(code);
    localStorage.setItem('selected_currency', code);
  };

  const convert = (amountUSD) => {
    if (!amountUSD || currency === 'USD' || !rates[currency]) return amountUSD;
    return amountUSD * rates[currency];
  };

  const formatPrice = (amountUSD) => {
    const converted = convert(amountUSD);
    const info = CURRENCIES.find(c => c.code === currency) || { symbol: '$', code: 'USD' };
    
    // Format based on currency decimals
    const noDecimalCurrencies = ['JPY', 'KRW', 'VND', 'IDR', 'CLP', 'HUF', 'ISK', 'IQD'];
    const decimals = noDecimalCurrencies.includes(currency) ? 0 : 2;
    
    return `${info.symbol}${converted.toFixed(decimals)}`;
  };

  return (
    <CurrencyContext.Provider value={{
      currency, changeCurrency, convert, formatPrice,
      currencies: CURRENCIES, loading, rates
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
